import React, { useState } from "react";
import { format } from "date-fns";
import { CheckCircle, Clock, MapPin, Package, FileText, Truck, FlaskConical, Microscope, FileCheck, X, Download } from "lucide-react";

const LabTracking = ({ trackingInfo }) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportUrl, setReportUrl] = useState("");
  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "dd MMM yyyy, hh:mm a");
  };

  const getStatusIcon = (status, isCompleted) => {
    const iconClass = isCompleted ? "w-6 h-6 text-green-500" : "w-6 h-6 text-gray-400";
    
    switch (status) {
      case "PLACED":
        return <Package className={iconClass} />;
      case "SAMPLE COLLECTION SCHEDULED":
        return <Clock className={iconClass} />;
      case "SAMPLE COLLECTED":
        return <FlaskConical className={iconClass} />;
      case "IN-TRANSIT TO LAB":
        return <Truck className={iconClass} />;
      case "DELIVERED TO LAB":
        return <MapPin className={iconClass} />;
      case "PROCESSING":
        return <Microscope className={iconClass} />;
      case "REPORT UPLOADED":
        return <FileCheck className={iconClass} />;
      default:
        return <CheckCircle className={iconClass} />;
    }
  };

  const getStatusColor = (status, isCompleted) => {
    if (isCompleted) {
      return "text-green-600";
    }
    return "text-gray-400";
  };

  const handleViewReport = () => {
    if (trackingInfo.labOrderDocs && trackingInfo.labOrderDocs.length > 0) {
      // Construct the full URL using the Google Cloud Storage API
      const baseUrl = process.env.REACT_APP_API_GOOGLE_URL  ;
      const reportPath = trackingInfo.labOrderDocs[0];
      
      // Ensure the report path doesn't already include the base URL
      const fullReportUrl = reportPath.startsWith('http') 
        ? reportPath 
        : `${baseUrl}${reportPath}`;
      
      setReportUrl(fullReportUrl);
      setShowReportModal(true);
    }
  };

  // const handleDownloadReport = () => {
  //   if (reportUrl) {
  //     // Create a temporary anchor element to trigger download
  //     const link = document.createElement('a');
  //     link.href = reportUrl;
      
  //     // Extract filename from the URL or use a default name
  //     const urlParts = reportUrl.split('/');
  //     const filename = urlParts[urlParts.length - 1] || 'lab-report.pdf';
  //     link.download = filename;
      
  //     // Append to body, click, and remove
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   }
  // };

  // Define the correct order of statuses for lab orders
  const statusOrder = [
    "PLACED",
    "SAMPLE COLLECTION SCHEDULED", 
    "SAMPLE COLLECTED",
    "IN-TRANSIT TO LAB",
    "DELIVERED TO LAB",
    "PROCESSING",
    "REPORT UPLOADED"
  ];

  // Process status history to show in correct order
  const processStatusHistory = (statusHistory) => {
    if (!statusHistory || !Array.isArray(statusHistory)) return [];
    
    // Create a map to get the latest occurrence of each status
    const statusMap = new Map();
    statusHistory.forEach(status => {
      const existingStatus = statusMap.get(status.status);
      const currentDate = new Date(status.statusDate);
      
      if (!existingStatus || new Date(existingStatus.statusDate) < currentDate) {
        statusMap.set(status.status, status);
      }
    });
    
    // Build the timeline in the correct order
    const orderedStatuses = [];
    statusOrder.forEach(statusName => {
      const status = statusMap.get(statusName);
      if (status) {
        orderedStatuses.push(status);
      }
    });
    
    return orderedStatuses;
  };

  if (!trackingInfo) return null;

  const processedStatusHistory = processStatusHistory(trackingInfo.statusHistory);

  return (
    <div className="w-full mb-4">
      <div className="bg-white w-full rounded-lg shadow-lg p-4 sm:p-6 overflow-y-auto ">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
          <div className="mb-4 sm:mb-0">
            <h2 className="text-lg sm:text-xl font-bold text-[#003F6B] mb-1">
              Order No: {trackingInfo.orderNo}
            </h2>
            <p className="text-sm text-gray-600">
              Service Type: {trackingInfo.serviceType}
            </p>
            <p className="text-sm sm:text-base font-semibold text-green-600">
              Current Status: {trackingInfo.latestStatus}
            </p>
          </div>
          {/* <button
            onClick={() => setShowModal(false)}
            className="text-2xl text-gray-500 hover:text-red-600"
          >
            &times;
          </button> */}
        </div>

        {/* View Report Button */}
        {trackingInfo.labOrderDocs && trackingInfo.labOrderDocs.length > 0 && (
          <div className="mb-6">
            <button
              onClick={handleViewReport}
              className="w-full sm:w-auto bg-gradient-to-r from-[#004172] to-[#3cba84] text-white px-4 sm:px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              View Report
            </button>
          </div>
        )}

        {/* Status Timeline */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-[#003F6B] mb-4">
            Order Status Timeline
          </h3>
          
          {/* Mobile Layout - Vertical */}
          <div className="relative sm:hidden">
            {processedStatusHistory.map((status, index) => {
              const isCompleted = true;
              const isLast = index === processedStatusHistory.length - 1;
              
              return (
                <div key={index} className="flex items-start mb-6 relative">
                  {/* Status Icon */}
                  <div className="flex-shrink-0 mr-4 z-10 bg-white">
                    {getStatusIcon(status.status, isCompleted)}
                  </div>
                  
                  {/* Status Content */}
                  <div className="flex-1">
                    <div className={`font-semibold text-sm ${getStatusColor(status.status, isCompleted)}`}>
                      {status.status}
                    </div>
                    {/* <div className="text-sm text-gray-500 mt-1">
                      {formatDate(status.statusDate)}
                    </div> */}
                  </div>
                  
                  {/* Connecting Line - Show for all except the last item */}
                  {!isLast && (
                    <div 
                      className={`absolute left-3 top-6 w-0.5 h-16 ${
                        isCompleted ? "bg-green-300" : "bg-gray-300"
                      }`}
                      style={{ zIndex: 1 }}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop Layout - Horizontal */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-between relative">
              {/* Horizontal connecting line */}
              <div className="absolute top-6 left-0 right-0 h-0.5 bg-green-300 z-0"></div>
              
              {processedStatusHistory.map((status, index) => {
                const isCompleted = true;
                
                return (
                  <div key={index} className="flex flex-col items-center relative z-10 bg-white px-2">
                    {/* Status Icon */}
                    <div className="mb-2">
                      {getStatusIcon(status.status, isCompleted)}
                    </div>
                    
                    {/* Status Content */}
                    <div className="text-center max-w-24">
                      <div className={`text-xs font-semibold ${getStatusColor(status.status, isCompleted)} mb-1`}>
                        {status.status.split(' ').slice(0, 2).join(' ')}
                      </div>
                      {/* <div className="text-xs text-gray-500">
                        {formatDate(status.statusDate)}
                      </div> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
       
      </div>

      {/* Report Viewer Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-[#003F6B]">
                Lab Report Viewer
              </h3>
              <div className="flex items-center gap-3">
                {/* <button
                  onClick={handleDownloadReport}
                  className="flex items-center gap-2 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300"
                  title="Download Report"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button> */}
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-red-600 transition-colors"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Report Content */}
            <div className="flex-1 p-4">
              {reportUrl && (
                <iframe
                  src={reportUrl}
                  className="w-full h-full border-0 rounded"
                  title="Lab Report"
                  onError={(e) => {
                    console.error("Error loading report:", e);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabTracking;
